import type {ImageCompressionTarget} from "./imageCompression";

/**
 * What each upload surface's endpoint will store (ui#601).
 *
 * These are MIRRORS of server constants, in the same sense as the validation modules beside each
 * picker: the api re-checks every upload and is the only thing that can refuse one for real. What they
 * buy is a client that produces an acceptable image instead of a sender who has to.
 *
 * They live together rather than beside each picker so that "does every picker compress, and to what?"
 * is one file to read. Two of them are deliberately the same object — see {@link BACKDROP_IMAGE_TARGET}.
 */

/**
 * The brand logo — `BrandLogos.MAX_BYTES` (512 KiB) and `BrandLogos.MAX_EDGE_PIXELS` (4000).
 *
 * Half the allowance of a backdrop, because a logo is composited onto every download page this account
 * sends: it is the one image a recipient always pays for.
 */
export const LOGO_IMAGE_TARGET: ImageCompressionTarget = {
	maxBytes: 512 * 1024,
	maxEdgePixels: 4000,
};

/**
 * Page-theme backdrops AND sponsor creatives — `SponsorAssets.MAX_BYTES` (1 MiB) and
 * `SponsorAssets.MAX_EDGE_PIXELS` (4000).
 *
 * One constant for both because it is one api rule: the two pickers write to the same asset store and
 * feed the same three treatments. A second constant here could only ever be the one that drifted.
 */
export const BACKDROP_IMAGE_TARGET: ImageCompressionTarget = {
	maxBytes: 1024 * 1024,
	maxEdgePixels: 4000,
};

/**
 * The account profile picture.
 *
 * Unlike the others this mirrors no per-image server rule — `/account/profile-image` validates neither
 * size nor dimensions, and the only ceiling is Spring's `spring.servlet.multipart.max-file-size`, 2 MB
 * for the whole multipart request. That made this the one picker with no guard at all: a photo straight
 * off a phone is 4–8 MB, so the surface answered the most ordinary input there is with a 413.
 *
 * 1 MiB rather than the 2 MB ceiling, because the ceiling covers the ENCODED multipart body — field
 * headers and base64-free but framed — and because an avatar is rendered at a few hundred pixels.
 */
export const PROFILE_PICTURE_TARGET: ImageCompressionTarget = {
	maxBytes: 1024 * 1024,
	maxEdgePixels: 4000,
};
